// VollmachtZeile.tsx — die drei Aktionen einer Vollmacht (Herunterladen ·
// Hochladen · Unterschreiben lassen) plus Status-/Datei-Chips. Reine Anzeige;
// was die Knöpfe tun, entscheidet die Karte.

import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";

export const useVollmachtStyles = makeStyles({
  zeile: { display: "flex", flexDirection: "column", rowGap: "8px" },
  titel: {
    fontFamily: REFINED.fontUI,
    fontWeight: 700,
    fontSize: `${REFINED.fs.md}px`,
    color: REFINED.ink,
  },
  hinweis: {
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.xs}px`,
    color: REFINED.ink3,
    marginTop: "-4px",
  },
  aktionen: { display: "flex", flexWrap: "wrap", columnGap: "8px", rowGap: "8px", alignItems: "center" },
  knopf: {
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: `${REFINED.fs.sm}px`,
    ...shorthands.padding("6px", "12px"),
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.aub50, ...shorthands.borderColor(REFINED.aub200), color: REFINED.aub },
    ":disabled": { cursor: "default", opacity: 0.55 },
  },
  knopfAktiv: {
    backgroundColor: REFINED.aub50,
    ...shorthands.borderColor(REFINED.aub),
    color: REFINED.aub,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("3px", "10px"),
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.xs}px`,
    fontWeight: 600,
    backgroundColor: REFINED.fieldBg,
    color: REFINED.ink2,
  },
  chipOk: { backgroundColor: REFINED.okBg, color: REFINED.ok },
  chipGold: { backgroundColor: REFINED.goldBg, color: REFINED.goldInk },
  chipX: {
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: "inherit",
    cursor: "pointer",
    ...shorthands.padding("0"),
    fontSize: `${REFINED.fs.sm}px`,
    lineHeight: 1,
  },
  meldung: { fontFamily: REFINED.fontUI, fontSize: `${REFINED.fs.xs}px`, color: REFINED.err },
  info: { fontFamily: REFINED.fontUI, fontSize: `${REFINED.fs.xs}px`, color: REFINED.ink2 },
  link: {
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.xs}px`,
    color: REFINED.aub,
    wordBreak: "break-all",
  },
});

export const VOLLMACHT_DATEI_ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,.tif,.tiff";

export type VollmachtAktionenProps = {
  onHerunterladen: () => void;
  /** Datei aus dem Windows-Explorer (Kontaktanlage: noch keine Akte, aus der man wählen könnte). */
  onHochladen?: (datei: File) => void;
  /**
   * Statt des Explorers: „Hochladen" öffnet die KMVS-Dokumentauswahl (in der
   * Akte, T-42). Gesetzt → kein Dateifeld.
   */
  onHochladenAuswahl?: () => void;
  onUnterschreiben: () => void;
  /** Knopf „Unterschreiben lassen" als gedrückt zeigen (Anlage: Link nach dem Speichern). */
  unterschreibenAktiv?: boolean;
  unterschreibenTitel?: string;
  unterschreibenGesperrt?: boolean;
  /** Hochladen sperren (z. B. solange keine Vollmacht gewählt ist). */
  hochladenGesperrt?: boolean;
  /** Tooltip für gesperrte Knöpfe, sagt, was zu tun ist. */
  gesperrtTitel?: string;
  laedt?: boolean;
  /** Zusätzliche Chips/Knöpfe rechts neben den drei Aktionen. */
  zusatz?: React.ReactNode;
  /** Beschriftung für Screenreader, z. B. der Vollmachtsname. */
  bezeichnung: string;
};

export const VollmachtAktionen: React.FC<VollmachtAktionenProps> = ({
  onHerunterladen,
  onHochladen,
  onHochladenAuswahl,
  onUnterschreiben,
  unterschreibenAktiv,
  unterschreibenTitel,
  unterschreibenGesperrt,
  hochladenGesperrt,
  gesperrtTitel,
  laedt,
  zusatz,
  bezeichnung,
}) => {
  const s = useVollmachtStyles();
  const input = React.useRef<HTMLInputElement>(null);
  return (
    <div className={s.aktionen} role="group" aria-label={`Aktionen ${bezeichnung}`}>
      <button type="button" className={s.knopf} onClick={onHerunterladen} disabled={laedt}>
        {laedt ? "Wird erzeugt…" : "Herunterladen"}
      </button>
      {!onHochladenAuswahl && (
      <input
        ref={input}
        type="file"
        accept={VOLLMACHT_DATEI_ACCEPT}
        style={{ display: "none" }}
        aria-label={`Datei für ${bezeichnung}`}
        onChange={(e) => {
          const datei = e.target.files?.[0];
          if (datei) onHochladen?.(datei);
          e.target.value = "";
        }}
      />
      )}
      <button
        type="button"
        className={s.knopf}
        onClick={() => (onHochladenAuswahl ? onHochladenAuswahl() : input.current?.click())}
        disabled={hochladenGesperrt}
        title={hochladenGesperrt ? gesperrtTitel : undefined}
      >
        Hochladen
      </button>
      <button
        type="button"
        className={mergeClasses(s.knopf, unterschreibenAktiv && s.knopfAktiv)}
        onClick={onUnterschreiben}
        title={unterschreibenGesperrt && gesperrtTitel ? gesperrtTitel : unterschreibenTitel}
        aria-pressed={unterschreibenAktiv ?? undefined}
        disabled={unterschreibenGesperrt}
      >
        {unterschreibenAktiv ? "✓ Unterschreiben lassen" : "Unterschreiben lassen"}
      </button>
      {zusatz}
    </div>
  );
};
